import { useEffect, useMemo, useRef, useState } from 'react';
import { Chip, Tooltip } from '@nextui-org/react';
import { motion } from 'framer-motion';
import {
  ChatMessage,
  ChatMessageWithAssistant,
  ChatMessageWithClient,
} from '../../types';
import ChatBubble, { ChatBubbleProps } from './bubble/ChatBubble';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentClient } from '../../store/client/clientSlice';
import {
  riskToLabel,
  selectRiskOfChats,
} from '../../store/assistant/assistantSlice';
import VerticalScrollContainer from '../base/VerticalScrollContainer';
import { getModels, getStringBetween, MOTION_KEYS } from '../../util';
import {
  selectActorConfig,
  selectAssistantConfig,
} from '../../store/settings/settingsSlice';
import { selectMessagesWithClient } from '../../store/chat/chatSlice';

export interface ChatBoxProps {
  messages: (ChatMessage | ChatMessageWithClient | ChatMessageWithAssistant)[];
  onTransfer?: (message: string, sendImmediately?: boolean) => void;
  bottomIndicator?: JSX.Element;
  emptyIndicator?: JSX.Element;
  isAssistant?: boolean;
  headerTitle?: string;
  headerSubtitle?: string;
  headerTag?: string;
  headerElements?: JSX.Element[];
  inputField?: JSX.Element;
}

export default function ChatBox({
  messages,
  onTransfer,
  bottomIndicator,
  emptyIndicator,
  isAssistant = false,
  headerTitle,
  headerSubtitle,
  headerTag,
  headerElements,
  inputField,
}: ChatBoxProps) {
  const currentClient = useAppSelector(selectCurrentClient);
  const riskOfChats = useAppSelector(selectRiskOfChats);
  const [messageCount, setMessageCount] = useState(0);
  const [scrollResetSignal, setScrollResetSignal] = useState(0);
  const [scrollBottomSignal, setScrollBottomSignal] = useState(0);
  const [currentTimestamp, setCurrentTimestamp] = useState(0);
  const models = getModels();

  const assistantConfig = useAppSelector(selectAssistantConfig);
  let selectedAssistantModel = '';
  for (const model of models) {
    if (model.tag === assistantConfig.modelTag) {
      selectedAssistantModel = model.label;
    }
  }

  const actorConfig = useAppSelector(selectActorConfig);
  let selectedActorModel = '';
  for (const model of models) {
    if (model.tag === actorConfig.modelTag) {
      selectedActorModel = model.label;
    }
  }

  const messagesWithClient = useAppSelector(selectMessagesWithClient);
  const messagesWithClientRef = useRef<any>();
  useEffect(() => {
    messagesWithClientRef.current = messagesWithClient;
  }, [messagesWithClient]);

  const riskLevel = useMemo(() => {
    const risk = currentClient?.userId
      ? riskOfChats[currentClient.userId]?.risk
      : undefined;

    // if (
    //   risk &&
    //   messagesWithClient &&
    //   messagesWithClientRef &&
    //   messagesWithClientRef.current &&
    //   messagesWithClient.length !== messagesWithClientRef.current.length &&
    //   currentClient
    // ) {
    //   if (messagesWithClient[messagesWithClient.length - 1]?.role !== 'user') {
    //     dispatch(deleteRiskOfChat(currentClient.userId));
    //   }
    // }

    return risk;
  }, [currentClient, riskOfChats]);

  const riskLabel = useMemo(
    () => (riskLevel ? riskToLabel(riskLevel) : undefined),
    [riskLevel]
  );

  const startTimestamp = useMemo(() => {
    if (messages.length === 0) {
      return 0;
    }
    return Date.parse(messages[0].createdAt) || 0;
  }, [messages]);

  const timeElapsed = useMemo(() => {
    const elapsed = (currentTimestamp - startTimestamp) / 1000; // in seconds
    if (elapsed < 1) {
      return null;
    }
    const seconds = Math.floor(elapsed % 60);
    const minutes = Math.floor((elapsed / 60) % 60);
    const hours = Math.floor((elapsed / 60 / 60) % 24);
    const days = Math.floor(elapsed / 60 / 60 / 24);
    return [
      days && `${days}d`,
      (days || hours) && `${hours}h`,
      (days || hours || minutes) && `${minutes}min`,
      (days || hours || minutes || seconds) && `${seconds}s`,
    ]
      .filter((i) => !!i)
      .join(' ');
  }, [startTimestamp, currentTimestamp]);

  useEffect(() => {
    if (startTimestamp === 0) {
      setCurrentTimestamp(0);
      return;
    }
    if (currentTimestamp === 0) {
      setCurrentTimestamp(Date.now());
    } else {
      setTimeout(() => setCurrentTimestamp(Date.now()), 1000);
    }
  }, [startTimestamp, currentTimestamp]);

  useEffect(() => {
    /**
     * hides scroll to bottom button on chat switch,
     * so that it doesn't appear in short conversations
     */
    setScrollResetSignal(Date.now());
  }, [currentClient]);

  useEffect(() => {
    const n = messages.length;
    if (n === 0) {
      return;
    }
    //scolls to bottom when there is a new message
    if (messageCount < n) {
      setScrollBottomSignal(Date.now());
      setMessageCount(n);
    }
  }, [messages, messageCount]);

  const emptyChat = useMemo(() => {
    if (isAssistant) {
      return "I'm an AI assistant to support you in your conversations with your clients. Chat with me naturally, or click one of the pre-defined functionalities listed below.";
    } else if (!isAssistant && currentClient?.userType === 'actor') {
      return "I'm an AI agent role-playing as a help seeker. Click Start below to start talking to me.";
    } else {
      return '';
    }
  }, [isAssistant, currentClient]);

  const chatHeader = (
    <motion.div
      key="chat-header"
      variants={{
        [MOTION_KEYS.INITIAL]: { y: '-150%' },
        [MOTION_KEYS.ANIMATE]: { y: 0 },
      }}
      initial={MOTION_KEYS.INITIAL}
      animate={MOTION_KEYS.ANIMATE}
      exit={MOTION_KEYS.INITIAL}
      className="flex flex-row items-center gap-2 p-2 border-b-1 backdrop-blur-md chat-header"
    >
      <div className="flex flex-row items-center gap-2 px-2">
        {(headerTitle || headerSubtitle) && (
          <div className="flex flex-col">
            {headerTitle && (
              <p
                key={headerTitle}
                className="text-lg font-semibold leading-tight"
              >
                {headerTitle}
              </p>
            )}
            {headerSubtitle && (
              <p className="text-xs text-default-500 leading-tight">
                {headerSubtitle}
              </p>
            )}
          </div>
        )}
        {isAssistant ||
          (riskLabel && (
            <motion.div
              key={currentClient?.userId}
              variants={{
                [MOTION_KEYS.INITIAL]: { scale: 0 },
                [MOTION_KEYS.ANIMATE]: { scale: riskLabel ? 1 : 0 },
              }}
            >
              <Chip size="sm" color={riskLabel.color} variant="flat">
                {riskLabel.content}
              </Chip>
            </motion.div>
          ))}

        {headerTag && (
          <Chip size="sm" color="secondary" variant="flat" className="w-fit">
            {headerTag}
          </Chip>
        )}

        {isAssistant && (
          <Chip size="sm" variant="flat">
            {selectedAssistantModel}
          </Chip>
        )}

        {!isAssistant && currentClient?.userType === 'actor' && (
          <Chip size="sm" variant="flat">
            {selectedActorModel}
          </Chip>
        )}

        {!isAssistant && timeElapsed && (
          <Tooltip content="Time elapsed since beginning the chat">
            <Chip size="sm" variant="flat">
              {timeElapsed}
            </Chip>
          </Tooltip>
        )}
      </div>
      {headerElements}
    </motion.div>
  );

  const chatInput = (
    <motion.div
      key="chat-input"
      variants={{
        [MOTION_KEYS.INITIAL]: { y: '150%' },
        [MOTION_KEYS.ANIMATE]: { y: 0 },
      }}
      initial={MOTION_KEYS.INITIAL}
      animate={MOTION_KEYS.ANIMATE}
      exit={MOTION_KEYS.INITIAL}
      className="relative p-2"
    >
      {inputField}
    </motion.div>
  );

  const bubbles =
    messages.length > 0 ? (
      <motion.div
        key="bubbles"
        variants={{
          [MOTION_KEYS.INITIAL]: {},
          [MOTION_KEYS.ANIMATE]: {
            transition: { staggerChildren: 0.05, delayChildren: 0.1 },
          },
          [MOTION_KEYS.EXIT]: {
            transition: { staggerChildren: 0.05, staggerDirection: -1 },
          },
        }}
        initial={MOTION_KEYS.INITIAL}
        animate={MOTION_KEYS.ANIMATE}
        exit={MOTION_KEYS.EXIT}
        className="flex flex-col w-full gap-4"
      >
        {messages.map((m, i) => {
          const isIncoming = m.role === 'assistant';
          const reasoningResponse = getStringBetween(
            m.content,
            '<<RESPONSE>>',
            '<</RESPONSE>>'
          );

          const props: ChatBubbleProps = {
            messageId: m.messageId,
            message: reasoningResponse === '' ? m.content : reasoningResponse,
            timestamp: m.createdAt,
            role: m.role,
            hue: isAssistant ? 'assistant' : 'client',
            showVoteButtons: isAssistant && isIncoming,
            showRiskIndicator: isIncoming,
            onTransfer,
          };
          if ('contentSentiment' in m) {
            props.sentiment = m.contentSentiment;
          }
          if ('promptType' in m) {
            props.promptType = m.promptType;
          }
          if ('modelTag' in m && m.role === 'assistant') {
            props.modelTag = m.modelTag;
          }
          if ('contentSuicideLabel' in m) {
            props.suicidalityLabel = m.contentSuicideLabel;
          }
          if ('contentSuicideScore' in m) {
            props.suicidalityScore = m.contentSuicideScore;
          }
          return <ChatBubble key={i} {...props} />;
        })}
      </motion.div>
    ) : (
      emptyIndicator || (
        <Chip
          size="sm"
          variant="flat"
          className="text-gray-600 self-center empty-chat"
        >
          {emptyChat}
        </Chip>
      )
    );

  const bottomIndicatorWrapped = bottomIndicator ? (
    <motion.div
      variants={{
        [MOTION_KEYS.INITIAL]: { opacity: 0 },
        [MOTION_KEYS.ANIMATE]: { opacity: 1 },
      }}
      initial={MOTION_KEYS.INITIAL}
      animate={MOTION_KEYS.ANIMATE}
      exit={MOTION_KEYS.INITIAL}
    >
      {bottomIndicator}
    </motion.div>
  ) : undefined;

  return (
    <VerticalScrollContainer
      topContent={chatHeader}
      bottomContent={chatInput}
      resetSignal={scrollResetSignal}
      scrollToBottomSignal={scrollBottomSignal}
    >
      <div className="flex flex-col w-full pt-4 px-4 gap-4">
        {bubbles}
        {bottomIndicatorWrapped}
      </div>
    </VerticalScrollContainer>
  );
}
